"use client"

import styled from "@emotion/styled"
import { Typography, useMediaQuery, useTheme } from "@mui/material"

import "swiper/css"
import "swiper/css/pagination"
import { Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { extractProperty } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

type Props = {
  data: any
}

const StyledDiv = styled.div`
  padding-top: 32px;
  padding-bottom: 48px;
  text-align: left;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 88px;
    padding-bottom: 64px;
    max-width: 1280px;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding-left: 16px;
  }
`

const StyledText = styled(Typography)`
  padding-top: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 24px;
  }
`

const YouTubeWrapper = styled.div`
  iframe {
    width: 100%;
    height: 440px;
    border: none;
    border-radius: 16px;
    ${({ theme }) => theme.breakpoints.down("md")} {
      height: 230px;
    }
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 64px;
  }
`

const MediaSliderWrapper = styled.div`
  margin-top: 24px;
`

const MediaImage = styled.img`
  height: 230px;
  width: 100%;
  object-fit: cover;
  border-radius: 8px;
`

const StyledWrapper = styled.div`
  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 1280px;
    padding: 0 88px;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    max-width: 1920px;
    padding: 0 200px;
  }
`

const StyledImagesWrapper = styled.div`
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 0 88px;
    padding-top: 64px;
    padding-right: 0px;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    padding: 0 200px;
    padding-right: 0px;
  }
`

// Helper to convert regular YouTube URL to embed URL
const getYouTubeEmbedUrl = (url: string): string => {
  if (url.includes("youtube.com/embed")) {
    return url
  }
  let videoId = ""
  const shortUrlMatch = url.match(/youtu\.be\/([^?&]+)/)
  const longUrlMatch = url.match(/v=([^?&]+)/)
  if (shortUrlMatch && shortUrlMatch[1]) {
    videoId = shortUrlMatch[1]
  } else if (longUrlMatch && longUrlMatch[1]) {
    videoId = longUrlMatch[1]
  }
  return videoId ? `https://www.youtube.com/embed/${videoId}` : url
}

// Custom styled Swiper for mobile with pagination bullet styles.
const MobileSwiper = styled(Swiper)`
  padding-bottom: 40px;
  .swiper-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .swiper-pagination-bullet {
    width: 6px;
    height: 6px;
    background: #d1d5db;
    opacity: 1;
    border-radius: 50%;
    margin: 0 2px !important;
  }
  .swiper-pagination-bullet-active {
    width: 8px;
    height: 8px;
    background: #1bbd77;
    border-radius: 50%;
  }
`

const MultyUrl = ({ data }: Props) => {
  const theme = useTheme()
  const isDesktop = !useMediaQuery(theme.breakpoints.down("md"))

  const title = extractProperty(data, "title")
  const text = extractProperty(data, "text")
  const itemsListRaw = extractProperty(data, "itemsList")
  const itemsList = Array.isArray(itemsListRaw) ? itemsListRaw : []
  const allMedia = itemsList.filter((item: any) => item.type === "MEDIA")
  const EXTERNAL = itemsList.filter((item: any) => item.type === "EXTERNAL")

  const embedUrl =
    EXTERNAL.length > 0 && EXTERNAL[0]?.url
      ? getYouTubeEmbedUrl(EXTERNAL[0].url)
      : null

  // For mobile, merge external (YouTube) and media slides.
  const mobileSlides = []
  if (embedUrl) {
    mobileSlides.push(
      <SwiperSlide key="external">
        <YouTubeWrapper>
          <iframe
            src={embedUrl}
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="YouTube video preview"
          />
        </YouTubeWrapper>
      </SwiperSlide>,
    )
  }
  allMedia.forEach((media: any, idx: number) => {
    mobileSlides.push(
      <SwiperSlide key={`media-${idx}`}>
        <MediaImage
          src={getUmbracoMediaUrl(media.url)}
          alt={media.name || "Media Image"}
        />
      </SwiperSlide>,
    )
  })
  if (!title && !text && allMedia.length === 0 && !embedUrl) {
    return null
  }
  return (
    <StyledDiv>
      <StyledWrapper>
        <Typography variant="h3">{title}</Typography>
        <StyledText variant="body1">{text}</StyledText>
        {/* On desktop: show external (YouTube) slide separately */}
        {isDesktop && embedUrl && (
          <YouTubeWrapper>
            <iframe
              src={embedUrl}
              allow="autoplay; encrypted-media"
              allowFullScreen
              title="YouTube video preview"
            />
          </YouTubeWrapper>
        )}
      </StyledWrapper>

      <StyledImagesWrapper>
        {!isDesktop ? (
          <MediaSliderWrapper>
            <MobileSwiper
              slidesPerView={1.3}
              spaceBetween={20}
              pagination={{ clickable: true }}
              modules={[Pagination]}
            >
              {mobileSlides}
            </MobileSwiper>
          </MediaSliderWrapper>
        ) : (
          allMedia.length > 0 && (
            <MediaSliderWrapper>
              <Swiper slidesPerView={4} spaceBetween={20}>
                {allMedia.map((media: any, idx: number) => (
                  <SwiperSlide key={idx}>
                    <MediaImage
                      src={getUmbracoMediaUrl(media.url)}
                      alt={media.name || "Media Image"}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </MediaSliderWrapper>
          )
        )}
      </StyledImagesWrapper>
    </StyledDiv>
  )
}

export default MultyUrl
